<template>
  <modal
    name="widget-settings"
    height="auto"
    :width="modalWidth"
    :adaptive="true"
    @before-close="onClose"
  >
    <div class="widget-settings-modal p-4" v-if="widget">
      <h5 class="mb-3">Edit Widget</h5>
      <p class="text-muted small mb-3">{{ typeLabel }}</p>

      <div class="form-group">
        <label class="small text-muted">Title (shown on card)</label>
        <input
          v-model="form.title"
          class="form-control"
          maxlength="80"
          :placeholder="titlePlaceholder"
        />
      </div>

      <hr class="my-3">
      <p class="small text-muted font-weight-bold mb-2">Appearance</p>
      <div v-for="field in appearanceFields" :key="'app-' + field.key" class="form-group">
        <label class="small text-muted">{{ field.label }}</label>
        <input
          v-model.number="form[field.key]"
          type="number"
          class="form-control"
          :min="field.min"
          :max="field.max"
        >
      </div>

      <hr v-if="fields.length" class="my-3">
      <p v-if="fields.length" class="small text-muted font-weight-bold mb-2">Widget options</p>

      <div v-for="field in fields" :key="field.key" class="form-group">
        <label class="small text-muted">{{ field.label }}</label>

        <select
          v-if="field.type === 'node'"
          v-model="form[field.key]"
          class="form-control"
        >
          <option value="">-- Select device --</option>
          <option v-for="n in nodes" :key="n.id" :value="n.id">{{ n.name }}</option>
        </select>

        <select
          v-else-if="field.type === 'node-or-all'"
          v-model="form[field.key]"
          class="form-control"
        >
          <option value="all">All</option>
          <option v-for="n in nodes" :key="n.id" :value="n.id">{{ n.name }}</option>
        </select>

        <select
          v-else-if="field.type === 'select'"
          v-model="form[field.key]"
          class="form-control"
        >
          <option v-for="opt in field.options" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>

        <div v-else-if="field.type === 'nodes'" class="border rounded p-2" style="max-height:160px;overflow:auto">
          <div v-if="!nodes.length" class="text-muted small">No devices</div>
          <div v-for="n in nodes" :key="n.id" class="custom-control custom-checkbox">
            <input
              :id="'ws-node-' + n.id"
              type="checkbox"
              class="custom-control-input"
              :value="n.id"
              :checked="(form[field.key] || []).includes(n.id)"
              @change="toggleNodeId(field.key, n.id, $event.target.checked)"
            >
            <label class="custom-control-label" :for="'ws-node-' + n.id">{{ n.name }}</label>
          </div>
        </div>

        <div v-else-if="field.type === 'boolean'" class="custom-control custom-switch">
          <input
            :id="'ws-bool-' + field.key"
            type="checkbox"
            class="custom-control-input"
            v-model="form[field.key]"
          >
          <label class="custom-control-label" :for="'ws-bool-' + field.key">Enabled</label>
        </div>

        <div v-else-if="field.type === 'checks'" class="border rounded p-2">
          <div v-for="opt in field.options" :key="opt.value" class="custom-control custom-checkbox">
            <input
              :id="'ws-check-' + field.key + '-' + opt.value"
              type="checkbox"
              class="custom-control-input"
              :value="opt.value"
              :checked="(form[field.key] || []).includes(opt.value)"
              @change="toggleCheck(field.key, opt.value, $event.target.checked)"
            >
            <label class="custom-control-label" :for="'ws-check-' + field.key + '-' + opt.value">
              {{ opt.label }}
            </label>
          </div>
        </div>

        <div v-else-if="field.type === 'audio-sources'" class="border rounded p-2" style="max-height:220px;overflow:auto">
          <p class="small text-muted mb-2">Kosong = tampilkan semua. Pilih device dulu.</p>
          <div v-if="!form.nodeId" class="text-muted small">Pilih device di atas.</div>
          <div v-else-if="audioInputsLoading" class="text-muted small">
            <i class="fas fa-spinner fa-spin" /> Loading sources…
          </div>
          <div v-else-if="audioInputsError" class="text-danger small">{{ audioInputsError }}</div>
          <template v-else>
            <div v-if="!audioSourceRows(field.key).length" class="text-muted small">Tidak ada audio source.</div>
            <div v-for="src in audioSourceRows(field.key)" :key="src.name" class="custom-control custom-checkbox">
              <input
                :id="'ws-audio-' + src.name"
                type="checkbox"
                class="custom-control-input"
                :value="src.name"
                :checked="(form[field.key] || []).includes(src.name)"
                @change="toggleCheck(field.key, src.name, $event.target.checked)"
              >
              <label class="custom-control-label" :for="'ws-audio-' + src.name">{{ src.name }}</label>
            </div>
            <div v-if="(form[field.key] || []).length" class="mt-2">
              <button type="button" class="btn btn-sm btn-link p-0" @click="$set(form, field.key, [])">Clear (all)</button>
            </div>
          </template>
        </div>

        <input
          v-else-if="field.type === 'number'"
          v-model.number="form[field.key]"
          type="number"
          class="form-control"
          :min="field.min"
          :placeholder="field.placeholder"
        >

        <textarea
          v-else-if="field.type === 'textarea'"
          v-model="form[field.key]"
          class="form-control"
          rows="5"
          :placeholder="field.placeholder"
        />

        <div v-else-if="field.type === 'shortcut-list'" class="shortcut-editor">
          <div
            v-for="(item, idx) in form[field.key]"
            :key="idx"
            class="shortcut-editor__row border rounded p-2 mb-2"
          >
            <div class="d-flex align-items-start mb-2">
              <span
                class="shortcut-editor__preview mr-2"
                :style="{ background: item.color, color: previewText(item.color) }"
              >
                <i class="fas" :class="'fa-' + (item.icon || 'bell')"></i>
              </span>
              <div class="flex-grow-1">
                <input
                  v-model="item.label"
                  class="form-control form-control-sm mb-1"
                  maxlength="80"
                  placeholder="Button label"
                >
                <input
                  v-model="item.message"
                  class="form-control form-control-sm"
                  maxlength="500"
                  placeholder="Message sent (optional, defaults to label)"
                >
              </div>
              <button
                type="button"
                class="btn btn-sm btn-link text-danger ml-1"
                title="Remove"
                @click="removeShortcut(field.key, idx)"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="form-row">
              <div class="col-6">
                <label class="small text-muted mb-1">Color</label>
                <div class="d-flex align-items-center">
                  <input v-model="item.color" type="color" class="shortcut-editor__color mr-2">
                  <div class="shortcut-editor__swatches">
                    <button
                      v-for="preset in colorPresets"
                      :key="preset.value"
                      type="button"
                      class="shortcut-editor__swatch"
                      :class="{ 'is-active': item.color === preset.value }"
                      :style="{ background: preset.value }"
                      :title="preset.label"
                      @click="item.color = preset.value"
                    />
                  </div>
                </div>
              </div>
              <div class="col-6">
                <label class="small text-muted mb-1">Icon</label>
                <select v-model="item.icon" class="form-control form-control-sm">
                  <option v-for="opt in iconOptions" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <button type="button" class="btn btn-sm btn-outline-primary" @click="addShortcut(field.key)">
            <i class="fas fa-plus"></i> Add shortcut
          </button>
        </div>

        <div v-else-if="field.type === 'macro-ids'" class="border rounded p-2" style="max-height:200px;overflow:auto">
          <p class="small text-muted mb-2">Kosong = tampilkan semua dari sumber yang dipilih.</p>
          <div v-if="!macroPickerList.length" class="text-muted small">
            Belum ada macro.
            <router-link to="/qlc-macros" @click.native="hide">QLC Macros</router-link>
            /
            <router-link to="/macros" @click.native="hide">Macros</router-link>
          </div>
          <div v-for="m in macroPickerList" :key="m.id" class="custom-control custom-checkbox">
            <input
              :id="'ws-macro-' + m.id"
              type="checkbox"
              class="custom-control-input"
              :value="m.id"
              :checked="(form[field.key] || []).map(Number).includes(m.id)"
              @change="toggleMacroId(field.key, m.id, $event.target.checked)"
            >
            <label class="custom-control-label" :for="'ws-macro-' + m.id">{{ m.name }}</label>
          </div>
        </div>

        <div v-else-if="field.type === 'qlc-button-ids'" class="qlc-picker">
          <p class="small text-muted mb-2">Kosong = tampilkan semua tombol. Pilih device dulu.</p>
          <div v-if="!form.nodeId" class="text-muted small">Pilih device di atas.</div>
          <div v-else-if="qlcButtonsLoading" class="text-muted small">
            <i class="fas fa-spinner fa-spin" /> Loading buttons…
          </div>
          <div v-else-if="qlcButtonsError" class="text-danger small">{{ qlcButtonsError }}</div>
          <div v-else-if="!qlcButtons.length" class="text-muted small">Tidak ada tombol di project QLC+.</div>
          <template v-else>
            <div class="qlc-picker__toolbar">
              <div class="input-group input-group-sm qlc-picker__search">
                <div class="input-group-prepend">
                  <span class="input-group-text"><i class="fas fa-search" /></span>
                </div>
                <input
                  v-model="qlcButtonSearch"
                  type="search"
                  class="form-control"
                  placeholder="Cari nama atau ID…"
                  autocomplete="off"
                >
                <div v-if="qlcButtonSearch" class="input-group-append">
                  <button type="button" class="btn btn-outline-secondary" title="Clear" @click="qlcButtonSearch = ''">
                    <i class="fas fa-times" />
                  </button>
                </div>
              </div>
              <div class="qlc-picker__actions">
                <span class="qlc-picker__count text-muted">
                  {{ qlcSelectedCount(field.key) }}/{{ qlcButtons.length }} selected
                  <template v-if="qlcButtonSearch.trim()">
                    · {{ filteredQlcButtons.length }} match
                  </template>
                </span>
                <button type="button" class="btn btn-sm btn-link p-0" @click="selectAllQlcButtons(field.key)">All</button>
                <button
                  type="button"
                  class="btn btn-sm btn-link p-0"
                  :disabled="!filteredQlcButtons.length"
                  @click="selectFilteredQlcButtons(field.key)"
                >Match</button>
                <button type="button" class="btn btn-sm btn-link p-0" @click="clearQlcButtons(field.key)">None</button>
              </div>
            </div>

            <div v-if="qlcSelectedButtons(field.key).length" class="qlc-picker__chips">
              <button
                v-for="b in qlcSelectedButtons(field.key)"
                :key="'chip-' + b.id"
                type="button"
                class="qlc-picker__chip"
                :title="'Remove ' + (b.name || b.id)"
                @click="toggleQlcButtonId(field.key, b.id, false)"
              >
                <span class="qlc-picker__chip-label">{{ b.name || `Button ${b.id}` }}</span>
                <i class="fas fa-times" />
              </button>
            </div>

            <div class="qlc-picker__list">
              <div v-if="!filteredQlcButtons.length" class="text-muted small px-1 py-2">Tidak ada yang cocok.</div>
              <label
                v-for="b in filteredQlcButtons"
                :key="b.id"
                class="qlc-picker__row"
                :class="{ 'is-on': qlcButtonChecked(field.key, b.id) }"
              >
                <input
                  type="checkbox"
                  :checked="qlcButtonChecked(field.key, b.id)"
                  @change="toggleQlcButtonId(field.key, b.id, $event.target.checked)"
                >
                <span class="qlc-picker__name">{{ b.name || `Button ${b.id}` }}</span>
                <span class="qlc-picker__id">{{ b.id }}</span>
              </label>
            </div>
          </template>
        </div>

        <input
          v-else
          v-model="form[field.key]"
          type="text"
          class="form-control"
          :placeholder="field.placeholder"
        >
      </div>

      <div class="d-flex justify-content-end mt-3">
        <button class="btn btn-light mr-2" @click="hide">Cancel</button>
        <button class="btn btn-primary" @click="save">Save</button>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { getWidgetFields, getWidgetTitle, getAppearanceFields } from '@/widgets/registry'
import {
  normalizeAlertShortcuts,
  DEFAULT_ALERT_SHORTCUTS,
  ALERT_COLOR_PRESETS,
  ALERT_ICON_OPTIONS,
  blankAlertShortcut,
  contrastText,
} from '@/widgets/alertShortcuts'
import { DEFAULT_APPEARANCE } from '@/widgets/appearance'
import { getQlcButtons, peekQlcButtons } from '@/widgets/qlcCache'
export default {
  data() {
    return {
      widget: null,
      form: {},
      colorPresets: ALERT_COLOR_PRESETS,
      iconOptions: ALERT_ICON_OPTIONS,
      qlcButtons: [],
      qlcButtonsLoading: false,
      qlcButtonsError: '',
      qlcButtonSearch: '',
      audioInputs: [],
      audioInputsLoading: false,
      audioInputsError: '',
    }
  },
  computed: {
    ...mapGetters('mission', ['nodes']),
    ...mapGetters('macro', { libraryMacros: 'generalMacros', qlcMacros: 'qlcMacros' }),
    fields() {
      return this.widget ? getWidgetFields(this.widget.type) : []
    },
    appearanceFields() {
      return getAppearanceFields()
    },
    typeLabel() {
      if (!this.widget) return ''
      return getWidgetTitle(this.widget.type)
    },
    titlePlaceholder() {
      const nodeId = this.form?.nodeId
      if (nodeId) {
        const node = (this.nodes || []).find((n) => n.id === nodeId)
        if (node?.name) return node.name
      }
      return this.typeLabel || 'Widget title'
    },
    macroPickerList() {
      if (this.widget?.type === 'qlc') {
        return this.form.macroFilter === 'all'
          ? [...(this.qlcMacros || []), ...(this.libraryMacros || [])]
          : (this.qlcMacros || [])
      }
      return this.libraryMacros || []
    },
    filteredQlcButtons() {
      const q = (this.qlcButtonSearch || '').trim().toLowerCase()
      if (!q) return this.qlcButtons
      return this.qlcButtons.filter((b) => {
        const name = String(b.name || '').toLowerCase()
        const id = String(b.id)
        return name.includes(q) || id.includes(q)
      })
    },
    modalWidth() {
      return this.fields.some((f) => (
        f.type === 'shortcut-list' || f.type === 'macro-ids' || f.type === 'qlc-button-ids'
      )) ? 600 : 440
    },
  },
  watch: {
    'form.nodeId'(id) {
      if (!this.widget) return
      if (this.widget.type === 'qlc' && this.fields.some((f) => f.type === 'qlc-button-ids')) {
        this.qlcButtonSearch = ''
        this.loadQlcButtons(id)
      }
      if (this.fields.some((f) => f.type === 'audio-sources')) {
        this.loadAudioInputs(id)
      }
    },
  },
  methods: {
    ...mapActions('workspace', ['updateWidgetConfig']),
    ...mapActions('macro', ['fetchMacros']),
    ...mapActions('mission', ['sendCommand']),
    previewText(color) {
      return contrastText(color)
    },
    async open(widget) {
      this.widget = widget
      this.qlcButtons = []
      this.qlcButtonsError = ''
      this.qlcButtonSearch = ''
      this.audioInputs = []
      this.audioInputsError = ''
      if (getWidgetFields(widget.type).some((f) => f.type === 'macro-ids' || widget.type === 'qlc')) {
        try { await this.fetchMacros() } catch (_) { /* ignore */ }
      }
      const config = { ...(widget.config || {}) }
      const defaultTitle = getWidgetTitle(this.widget.type)
      const form = {
        title: (config.title && config.title !== defaultTitle) ? config.title : '',
        fontSize: config.fontSize != null ? Number(config.fontSize) : DEFAULT_APPEARANCE.fontSize,
        titleFontSize: config.titleFontSize != null ? Number(config.titleFontSize) : DEFAULT_APPEARANCE.titleFontSize,
        displayFontSize: config.displayFontSize != null ? Number(config.displayFontSize) : DEFAULT_APPEARANCE.displayFontSize,
      }
      getWidgetFields(widget.type).forEach((field) => {
        if (field.type === 'nodes' || field.type === 'macro-ids' || field.type === 'qlc-button-ids' || field.type === 'audio-sources') {
          form[field.key] = Array.isArray(config[field.key]) ? [...config[field.key]] : []
        } else if (field.type === 'checks') {
          if (Array.isArray(config[field.key]) && config[field.key].length) {
            form[field.key] = [...config[field.key]]
          } else if (Array.isArray(field.default)) {
            form[field.key] = [...field.default]
          } else {
            form[field.key] = (field.options || []).map((o) => o.value)
          }
        } else if (field.type === 'boolean') {
          if (config[field.key] == null) {
            if (field.default != null) form[field.key] = !!field.default
            else form[field.key] = !(field.key === 'showTimestamp' || field.key === 'showMacros')
          } else {
            form[field.key] = !!config[field.key]
          }
        } else if (field.type === 'number') {
          if (config[field.key] != null) {
            form[field.key] = Number(config[field.key])
          } else if (field.default != null) {
            form[field.key] = Number(field.default)
          } else {
            form[field.key] = field.min || 0
          }
          if (field.key === 'historyPoints' && config[field.key] == null) form[field.key] = 20
        } else if (field.type === 'node-or-all') {
          form[field.key] = config[field.key] || 'all'
        } else if (field.type === 'select' && field.options?.length) {
          form[field.key] = config[field.key] ?? field.options[0].value
        } else if (field.type === 'textarea') {
          if (Array.isArray(config[field.key])) {
            form[field.key] = config[field.key].join('\n')
          } else if (config[field.key] != null) {
            form[field.key] = String(config[field.key])
          } else {
            form[field.key] = field.default || ''
          }
        } else if (field.type === 'shortcut-list') {
          const list = config[field.key] == null
            ? DEFAULT_ALERT_SHORTCUTS
            : normalizeAlertShortcuts(config[field.key])
          form[field.key] = list.map((s) => ({ ...s }))
        } else {
          form[field.key] = config[field.key] != null ? config[field.key] : ''
        }
      })
      this.form = form
      this.$modal.show('widget-settings')
      if (widget.type === 'qlc' && form.nodeId) {
        this.loadQlcButtons(form.nodeId)
      }
      if (getWidgetFields(widget.type).some((f) => f.type === 'audio-sources') && form.nodeId) {
        this.loadAudioInputs(form.nodeId)
      }
    },
    hide() {
      this.$modal.hide('widget-settings')
    },
    onClose() {
      this.widget = null
      this.form = {}
      this.qlcButtons = []
      this.qlcButtonsError = ''
      this.audioInputs = []
      this.audioInputsError = ''
    },
    parseCommandPayload(res) {
      const msg = res && res.result
      if (!msg) return null
      if (msg.type === 'error' || msg.error) {
        throw new Error(msg.error || 'Command failed')
      }
      let payload = msg.payload
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        } catch (e) {
          return null
        }
      }
      return payload
    },
    async loadQlcButtons(nodeId, force = false) {
      if (!nodeId) {
        this.qlcButtons = []
        this.qlcButtonsError = ''
        return
      }
      const cached = !force ? peekQlcButtons(nodeId) : null
      if (cached) {
        this.qlcButtons = cached
        this.qlcButtonsError = ''
        return
      }
      this.qlcButtonsLoading = true
      this.qlcButtonsError = ''
      try {
        this.qlcButtons = await getQlcButtons(
          (payload) => this.sendCommand(payload),
          nodeId,
          { force: !!force },
        )
      } catch (e) {
        this.qlcButtons = []
        this.qlcButtonsError = e.message || 'Failed to load QLC buttons'
      } finally {
        this.qlcButtonsLoading = false
      }
    },
    toggleNodeId(key, id, checked) {
      const list = [...(this.form[key] || [])]
      const idx = list.indexOf(id)
      if (checked && idx === -1) list.push(id)
      if (!checked && idx !== -1) list.splice(idx, 1)
      this.$set(this.form, key, list)
    },
    toggleCheck(key, value, checked) {
      const list = [...(this.form[key] || [])]
      const idx = list.indexOf(value)
      if (checked && idx === -1) list.push(value)
      if (!checked && idx !== -1) list.splice(idx, 1)
      this.$set(this.form, key, list)
    },
    audioSourceRows(key) {
      const names = this.audioInputs.map((i) => i.name)
      const selected = this.form[key] || []
      const extra = selected.filter((n) => !names.includes(n)).map((name) => ({ name }))
      return extra.concat(this.audioInputs)
    },
    async loadAudioInputs(nodeId) {
      if (!nodeId) {
        this.audioInputs = []
        this.audioInputsError = ''
        return
      }
      this.audioInputsLoading = true
      this.audioInputsError = ''
      try {
        const res = await this.sendCommand({
          node_id: nodeId,
          adapter: 'obs',
          action: 'GetAudioInputs',
          payload: {},
        })
        const data = this.parseCommandPayload(res) || {}
        const inputs = Array.isArray(data.inputs) ? data.inputs : []
        this.audioInputs = inputs
          .map((inp) => ({ name: String(inp.name || inp.Name || '').trim() }))
          .filter((inp) => inp.name)
      } catch (e) {
        this.audioInputs = []
        this.audioInputsError = e.message || 'Failed to load audio sources'
      } finally {
        this.audioInputsLoading = false
      }
    },
    addShortcut(key) {
      const list = [...(this.form[key] || []), blankAlertShortcut()]
      this.$set(this.form, key, list)
    },
    removeShortcut(key, idx) {
      const list = [...(this.form[key] || [])]
      list.splice(idx, 1)
      this.$set(this.form, key, list)
    },
    toggleMacroId(key, id, checked) {
      const list = [...(this.form[key] || [])].map(Number)
      const idx = list.indexOf(Number(id))
      if (checked && idx === -1) list.push(Number(id))
      if (!checked && idx !== -1) list.splice(idx, 1)
      this.$set(this.form, key, list)
    },
    qlcButtonChecked(key, id) {
      return (this.form[key] || []).map(Number).includes(Number(id))
    },
    qlcSelectedCount(key) {
      return (this.form[key] || []).length
    },
    qlcSelectedButtons(key) {
      const set = new Set((this.form[key] || []).map(Number))
      if (!set.size) return []
      const byId = new Map(this.qlcButtons.map((b) => [Number(b.id), b]))
      return [...set].map((id) => byId.get(id) || { id, name: `Button ${id}` })
    },
    toggleQlcButtonId(key, id, checked) {
      const list = [...(this.form[key] || [])].map(Number)
      const n = Number(id)
      const idx = list.indexOf(n)
      if (checked && idx === -1) list.push(n)
      if (!checked && idx !== -1) list.splice(idx, 1)
      this.$set(this.form, key, list)
    },
    selectAllQlcButtons(key) {
      this.$set(this.form, key, this.qlcButtons.map((b) => Number(b.id)))
    },
    selectFilteredQlcButtons(key) {
      const set = new Set((this.form[key] || []).map(Number))
      this.filteredQlcButtons.forEach((b) => set.add(Number(b.id)))
      this.$set(this.form, key, [...set])
    },
    clearQlcButtons(key) {
      this.$set(this.form, key, [])
    },
    async save() {
      if (!this.widget) return
      const config = { ...this.form }
      getWidgetFields(this.widget.type).forEach((field) => {
        if (field.type === 'textarea') {
          config[field.key] = String(config[field.key] || '')
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean)
        } else if (field.type === 'shortcut-list') {
          config[field.key] = normalizeAlertShortcuts(config[field.key] || [])
        } else if (field.type === 'macro-ids' || field.type === 'qlc-button-ids') {
          config[field.key] = (config[field.key] || []).map(Number).filter((n) => Number.isFinite(n))
        }
      })
      getAppearanceFields().forEach((field) => {
        const min = field.min ?? 10
        const max = field.max ?? 96
        const fallback = field.default ?? min
        let n = Number(config[field.key])
        if (!Number.isFinite(n)) n = fallback
        config[field.key] = Math.min(max, Math.max(min, Math.round(n)))
      })
      const defaultTitle = getWidgetTitle(this.widget.type)
      const trimmed = String(config.title || '').trim()
      const nodeName = this.titlePlaceholder
      if (!trimmed || trimmed === defaultTitle || trimmed === nodeName) {
        config.title = ''
      } else {
        config.title = trimmed
      }
      try {
        await this.updateWidgetConfig({ widgetId: this.widget.id, config })
        this.hide()
      } catch (e) {
        this.$toast?.error?.('Failed to save widget') || alert('Failed to save widget')
      }
    },
  },
}
</script>

<style scoped>
.qlc-picker {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.65rem;
  background: #fff;
}
.qlc-picker__toolbar {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
}
.qlc-picker__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.8rem;
}
.qlc-picker__count {
  margin-right: auto;
  font-size: 0.75rem;
}
.qlc-picker__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  max-height: 72px;
  overflow: auto;
  margin-bottom: 0.45rem;
  padding-bottom: 0.15rem;
}
.qlc-picker__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  max-width: 100%;
  border: 1px solid rgba(18, 140, 126, 0.35);
  background: rgba(18, 140, 126, 0.1);
  color: #0f766e;
  border-radius: 999px;
  padding: 0.15rem 0.45rem 0.15rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
}
.qlc-picker__chip-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.qlc-picker__list {
  max-height: 220px;
  overflow: auto;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: #fafbfc;
}
.qlc-picker__row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.45rem;
  margin: 0;
  padding: 0.35rem 0.5rem;
  border-bottom: 1px solid #eef0f2;
  font-size: 0.8rem;
  cursor: pointer;
}
.qlc-picker__row:last-child {
  border-bottom: 0;
}
.qlc-picker__row:hover {
  background: rgba(37, 99, 235, 0.06);
}
.qlc-picker__row.is-on {
  background: rgba(18, 140, 126, 0.08);
}
.qlc-picker__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.qlc-picker__id {
  color: #6c757d;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
}
.shortcut-editor__row {
  background: var(--ws-bg-subtle, #f8f9fa);
}
.shortcut-editor__preview {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.shortcut-editor__color {
  width: 36px;
  height: 30px;
  padding: 0;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
}
.shortcut-editor__swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.shortcut-editor__swatch {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid transparent;
  padding: 0;
  cursor: pointer;
}
.shortcut-editor__swatch.is-active {
  border-color: #212529;
  box-shadow: 0 0 0 1px #fff inset;
}
</style>
